import { HELP_CENTER_URL } from '@aloft/toolkit-shared';
import { Block, Button, Flex, ModeSwitcher, Tabs, Typography } from '@aloft/toolkit-sui';

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// eslint-disable-next-line aloft/prefer-sound-ui-button
import { CraftsmanAddCompButtons } from '../../../components/CraftsmanAddCompButtons.tsx';
import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { useTkv2HandleIconClick } from '../../../components/ReferenceBar/useTkv2HandleIconClick.ts';
import { SettingsControls } from '../../../components/Settings/SettingsControls.tsx';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import {
  selectIncludeCostApproach,
  selectOrder,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectAivArvActiveTab } from '../../../redux/features/selectors/uiSelectors.ts';
import { getAivArvTabStorageKey, setAivArvActiveTab } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { AivArvActiveTab } from '../../../redux/types.ts';
import { CostMultiplierModal } from '../CostMultiplierModal/CostMultiplierModal.tsx';
import { VIEW_MODES } from '../consts.ts';
import { ViewMode } from '../types.ts';

type AnalysisToolbarProps = {
  viewMode: ViewMode;
  setViewMode: (val: ViewMode) => void;
};
const { selectShouldShowFeatureGrid } = featuresSelectors;

export const AnalysisToolbar = ({ viewMode, setViewMode }: AnalysisToolbarProps) => {
  const dispatch = useAppDispatch();
  const [isCostMultiplierOpen, setIsCostMultiplierOpen] = useState(false);
  const handleIconClick = useTkv2HandleIconClick();
  const shouldShowFeatureGrid = useAppSelector(selectShouldShowFeatureGrid);
  const { hasArvOption, id: orderId } = useAppSelector(selectOrder);
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const activeTab = useAppSelector(selectAivArvActiveTab);

  useEffect(() => {
    // Only save to localStorage when order ID is available
    if (orderId) {
      try {
        const storageKey = getAivArvTabStorageKey(orderId);
        window.localStorage.setItem(storageKey, activeTab);
      } catch {
        // Ignore localStorage errors (e.g., in private browsing mode)
      }
    }
  }, [activeTab, orderId]);

  const tabs: { label: string; value: AivArvActiveTab }[] = [
    { label: 'All', value: 'all' },
    { label: 'As-Is Value', value: 'aiv' },
    { label: 'After-Repair Value', value: 'arv' },
  ];

  return (
    <Flex
      className="toolbar-container"
      flexDirection="column"
      flexShrink={1}
      paddingTop="0.5rem"
      backgroundColor="surface1"
      gap="0px"
    >
      <Flex alignItems="center" gap="1.5rem" paddingX="24px" paddingY="8px" flexWrap="wrap">
        <GuidedTourTooltip
          text={[
            'Click on adjustments to view the results of your analysis. You can also view the adjustments in the Adjustment Grid or Commentary tabs.',
            'You can view the cost approach on the Cost Approach tab.',
          ]}
          tourID="analysisTour"
          tourIndex={10}
          placement="bottom"
        >
          <ModeSwitcher
            items={Object.entries(VIEW_MODES).map(([id, label]) => ({ id, label }))}
            value={viewMode}
            variant="secondary"
            size="sm"
            onChange={(newViewMode) => setViewMode(newViewMode as ViewMode)}
          />
        </GuidedTourTooltip>
        <Block width="1px" backgroundColor="strokeDefault" height="1rem" marginX="1rem" />
        {!shouldShowFeatureGrid && <CraftsmanAddCompButtons />}
        <Block>
          <Button
            variant="secondary"
            size="sm"
            text="Cost Multiplier"
            onClick={() => {
              setIsCostMultiplierOpen(true);
            }}
          />
          {isCostMultiplierOpen && (
            <CostMultiplierModal onClose={() => setIsCostMultiplierOpen(false)} />
          )}
        </Block>
        {includeCostApproach && (
          <Button
            variant="secondary"
            size="sm"
            text="Site Analysis"
            onClick={() => handleIconClick('site-analysis')}
          />
        )}
        <Link target="_blank" to={HELP_CENTER_URL}>
          <Typography variant="buttonRegular" color="buttonDefault">
            Quality Guide
          </Typography>
        </Link>
        <Block flexGrow={1} />
        <Block marginRight="1rem">
          <SettingsControls />
        </Block>
      </Flex>
      {hasArvOption && (
        <Flex paddingTop="0.5rem" width="100%">
          <Tabs
            tabs={tabs}
            activeTab={activeTab}
            setActiveTab={(tab: AivArvActiveTab) => dispatch(setAivArvActiveTab(tab))}
            variant="primary"
          />
        </Flex>
      )}
    </Flex>
  );
};
